import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

export interface HistoryCardState {
  isSelected: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class StateHistoryCardsService {
  // Map anidado: Map<imei, Map<id-type-variant, isSelected>>
  private state = new BehaviorSubject<Map<string, Map<string, boolean>>>(
    new Map()
  );
  private isLoading = new BehaviorSubject<boolean>(false);

  state$ = this.state.asObservable();
  isLoading$ = this.isLoading.asObservable();

  constructor() {}

  /**
   * Obtiene todos los estados de las tarjetas como array plano
   */
  get getStatesHistoryCards() {
    return this.state$.pipe(
      map((state) => {
        const allStates: Array<{
          imei: string;
          key: string;
          isSelected: boolean;
        }> = [];
        state.forEach((imeiMap, imei) => {
          imeiMap.forEach((isSelected, key) => {
            allStates.push({ imei, key, isSelected });
          });
        });
        return allStates;
      })
    );
  }

  /**
   * Obtiene el estado de una tarjeta específica por imei y key
   */
  getStateByKey(imei: string, key: string): boolean {
    const imeiMap = this.state.value.get(imei);
    return imeiMap?.get(key) ?? false;
  }

  /**
   * Obtiene todas las tarjetas seleccionadas de un IMEI específico
   */
  getSelectedCardsByImei(imei: string) {
    return this.state$.pipe(
      map((state) => {
        const imeiMap = state.get(imei);
        if (!imeiMap) return [];

        const selectedKeys: string[] = [];
        imeiMap.forEach((isSelected, key) => {
          if (isSelected) selectedKeys.push(key);
        });
        return selectedKeys;
      })
    );
  }

  /**
   * Obtiene la tarjeta seleccionada de un tipo específico para un IMEI
   */
  getSelectedCardByType(imei: string, type: string): string | undefined {
    const imeiMap = this.state.value.get(imei);
    if (!imeiMap) return undefined;

    for (const [key, isSelected] of imeiMap) {
      if (isSelected && key.includes(`-${type}-`)) {
        return key;
      }
    }
    return undefined;
  }

  /**
   * Selecciona una tarjeta específica y deselecciona las demás del mismo tipo para un IMEI
   */
  selectCard(imei: string, id: number, type: string, variant: string): void {
    const currentState = new Map(this.state.value);
    const key = `${id}-${type}-${variant}`;

    // Obtener o crear el Map del IMEI
    let imeiMap = currentState.get(imei);
    if (!imeiMap) {
      imeiMap = new Map<string, boolean>();
      currentState.set(imei, imeiMap);
    }

    // Deseleccionar todas las tarjetas del mismo tipo para este IMEI
    imeiMap.forEach((isSelected, cardKey) => {
      if (cardKey.includes(`-${type}-`)) {
        imeiMap!.set(cardKey, false);
      }
    });

    // Seleccionar la tarjeta específica
    imeiMap.set(key, true);

    this.state.next(currentState);
  }

  /**
   * Deselecciona una tarjeta específica para un IMEI
   */
  deselectCard(imei: string, id: number, type: string, variant: string): void {
    const currentState = new Map(this.state.value);
    const imeiMap = currentState.get(imei);
    const key = `${id}-${type}-${variant}`;

    if (imeiMap && imeiMap.has(key)) {
      imeiMap.set(key, false);
      this.state.next(currentState);
    }
  }

  /**
   * Toggle del estado de selección de una tarjeta para un IMEI
   */
  toggleCardSelection(
    imei: string,
    id: number,
    type: string,
    variant: string
  ): void {
    const key = `${id}-${type}-${variant}`;
    const currentState = this.state.value.get(imei)?.get(key);

    if (currentState) {
      // Si ya está seleccionada, la deseleccionamos
      this.deselectCard(imei, id, type, variant);
    } else {
      // Si no está seleccionada, la seleccionamos (y deseleccionamos las demás del mismo tipo)
      this.selectCard(imei, id, type, variant);
    }
  }

  /**
   * Agrega una nueva tarjeta al estado para un IMEI (sin seleccionar)
   */
  addCard(imei: string, id: number, type: string, variant: string): void {
    const currentState = new Map(this.state.value);
    const key = `${id}-${type}-${variant}`;

    // Obtener o crear el Map del IMEI
    let imeiMap = currentState.get(imei);
    if (!imeiMap) {
      imeiMap = new Map<string, boolean>();
      currentState.set(imei, imeiMap);
    }

    if (!imeiMap.has(key)) {
      imeiMap.set(key, false);
      this.state.next(currentState);
    }
  }

  /**
   * Agrega múltiples tarjetas al estado para un IMEI
   */
  addMultipleCards(
    imei: string,
    cards: Array<{ id: number; type: string; variant: string }>
  ): void {
    const currentState = new Map(this.state.value);

    // Obtener o crear el Map del IMEI
    let imeiMap = currentState.get(imei);
    if (!imeiMap) {
      imeiMap = new Map<string, boolean>();
      currentState.set(imei, imeiMap);
    }

    cards.forEach((card) => {
      const key = `${card.id}-${card.type}-${card.variant}`;
      if (!imeiMap!.has(key)) {
        imeiMap!.set(key, false);
      }
    });

    this.state.next(currentState);
  }

  /**
   * Remueve una tarjeta del estado para un IMEI
   */
  removeCard(imei: string, id: number, type: string, variant: string): void {
    const currentState = new Map(this.state.value);
    const imeiMap = currentState.get(imei);
    const key = `${id}-${type}-${variant}`;

    if (imeiMap && imeiMap.has(key)) {
      imeiMap.delete(key);
      this.state.next(currentState);
    }
  }

  /**
   * Limpia todas las tarjetas de un tipo específico para un IMEI
   */
  clearCardsByType(imei: string, type: string): void {
    const currentState = new Map(this.state.value);
    const imeiMap = currentState.get(imei);

    if (imeiMap) {
      imeiMap.forEach((isSelected, key) => {
        if (key.includes(`-${type}-`)) {
          imeiMap.delete(key);
        }
      });
      this.state.next(currentState);
    }
  }

  /**
   * Limpia todas las tarjetas de un IMEI específico
   */
  clearCardsByImei(imei: string): void {
    const currentState = new Map(this.state.value);
    currentState.delete(imei);
    this.state.next(currentState);
  }

  /**
   * Limpia todo el estado
   */
  clearAllCards(): void {
    this.state.next(new Map());
  }

  /**
   * Verifica si una tarjeta específica está seleccionada para un IMEI
   */
  isCardSelected(
    imei: string,
    id: number,
    type: string,
    variant: string
  ): boolean {
    const imeiMap = this.state.value.get(imei);
    if (!imeiMap) return false;

    const key = `${id}-${type}-${variant}`;
    return imeiMap.get(key) ?? false;
  }

  /**
   * Obtiene el número total de tarjetas para un IMEI específico
   */
  getTotalCardsByImei(imei: string): number {
    const imeiMap = this.state.value.get(imei);
    return imeiMap ? imeiMap.size : 0;
  }

  /**
   * Obtiene el número total de tarjetas en todos los IMEIs
   */
  getTotalCards(): number {
    let total = 0;
    this.state.value.forEach((imeiMap) => {
      total += imeiMap.size;
    });
    return total;
  }

  /**
   * Obtiene el número de tarjetas seleccionadas para un IMEI específico
   */
  getSelectedCardsCountByImei(imei: string): number {
    const imeiMap = this.state.value.get(imei);
    if (!imeiMap) return 0;

    let count = 0;
    imeiMap.forEach((isSelected) => {
      if (isSelected) count++;
    });
    return count;
  }

  /**
   * Obtiene el número total de tarjetas seleccionadas en todos los IMEIs
   */
  getSelectedCardsCount(): number {
    let total = 0;
    this.state.value.forEach((imeiMap) => {
      imeiMap.forEach((isSelected) => {
        if (isSelected) total++;
      });
    });
    return total;
  }

  /**
   * Obtiene todos los IMEIs que tienen tarjetas
   */
  getImeisWithCards(): string[] {
    return Array.from(this.state.value.keys());
  }
}
